// src/pages/Favoritos.tsx
/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useState, useEffect, useRef } from "react";
import {
  fetchFavoritos,
  toggleFavorito,
  updateEstadoFavorito,
  Favorito,
  FetchFavoritosParams,
} from "../api/favoritosApi";
import { useAuth, getAuthToken } from "../security/AuthLogin";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import LoadingLottie from "../components/LoadingLottie";
import AmountDropdown from "../components/amountDropdown";

import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faHeart,
  faLink,
  faLandmark,
  faMapMarkerAlt,
  faUser,
  faFileExcel,
  faSpinner,
  faCheck,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useSearchParams } from "react-router-dom";

/* -------------------------------------------------- */
/* -------------------  TIPOS  ----------------------- */
/* -------------------------------------------------- */

interface IFavoritoConMensajes extends Favorito {
  ultimo_emisor?: string | null;
  ultima_fecha_msg?: string | null;
  leido?: number;
  num_mensajes?: number;
  dueno_nombre?: string | null;
}

/* -------------------------------------------------- */
/* ------------------  HELPERS  ---------------------- */
/* -------------------------------------------------- */

/** Texto visible del estado del soporte */
const mapEstadoSoporte = (estado?: string | null): string => {
  if (!estado) return "En estudio";

  switch (estado.toLowerCase()) {
    case "pendiente": // <-- NUEVO
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

/** Slug para la clase CSS (`estado-*`) */
const slugSoporte = (estado?: string | null): string => {
  if (
    !estado ||
    estado.toLowerCase() === "pendiente" ||
    estado.toLowerCase() === "en estudio"
  ) {
    return "pendiente"; // <-- mantiene .estado-pendiente
  }
  switch (estado.toLowerCase()) {
    case "valido":
      return "viable";
    case "rechazado":
      return "no-viable";
    case "oculto":
      return "oculto";
    case "norecomendado":
      return "no-recomendado";
    default:
      return estado.toLowerCase().replace(/ /g, "-");
  }
};

/** Orden visual según emisor y rol */
const getEmisorPriority = (fav: IFavoritoConMensajes, role: string): number => {
  if (role === "cliente") {
    if (fav.ultimo_emisor === "nadie") return 1;
    if (fav.ultimo_emisor === "admin_tecnico") return 2;
    return 3;
  }
  if (fav.ultimo_emisor === "nadie") return 1;
  if (fav.ultimo_emisor === "cliente") return 2;
  return 3;
};

/** Color “glow” del wrapper */
const getBackgroundColor = (
  fav: IFavoritoConMensajes,
  role: string
): string => {
  if (fav.estado_soporte?.toLowerCase() === "oculto") return "black";

  const leidoVal = Number(fav.leido);
  if (role === "cliente") {
    if (fav.ultimo_emisor === "cliente") return "rgb(255,0,0)";
    if (fav.ultimo_emisor === "admin_tecnico")
      return leidoVal === 0 ? "rgb(255,255,0)" : "white";
    return "rgb(255,0,0)";
  }
  if (fav.ultimo_emisor === "cliente" || fav.ultimo_emisor === "nadie")
    return "rgb(255,0,0)";
  if (fav.ultimo_emisor === "admin_tecnico") return "rgb(0,255,0)";
  return "white";
};

/* -------------------------------------------------- */
/* ---------------- COMPONENTE ----------------------- */
/* -------------------------------------------------- */

const Favoritos: React.FC = () => {
  const { userRole } = useAuth();

  /* ---------- ESTADOS ---------- */
  const [favoritos, setFavoritos] = useState<IFavoritoConMensajes[] | null>(
    null
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"abiertos" | "cerrados">("abiertos");

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);
  const maxPages = Math.max(Math.ceil(total / limit), 1);

  /* filtros local-UI */
  const [localSearch, setLocalSearch] = useState("");
  const [localFechaInicio, setLocalFechaInicio] = useState("");
  const [localFechaFin, setLocalFechaFin] = useState("");
  const [localImporteDesde, setLocalImporteDesde] = useState("");
  const [localImporteHasta, setLocalImporteHasta] = useState("");
  const [localAutor, setLocalAutor] = useState("");
  const [localEstado, setLocalEstado] = useState("");
  const [localAsignado, setLocalAsignado] = useState("");
  const [localNoRespondidos, setLocalNoRespondidos] = useState(false);

  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return "No hay fecha límite";

    try {
      const fecha = new Date(fechaStr);

      if (isNaN(fecha.getTime())) {
        throw new Error("Fecha inválida");
      }

      const day = fecha.getUTCDate().toString().padStart(2, "0");
      const month = (fecha.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = fecha.getUTCFullYear();
      const hours = fecha.getUTCHours().toString().padStart(2, "0");
      const minutes = fecha.getUTCMinutes().toString().padStart(2, "0");

      return `${day}/${month}/${year} a las ${hours}:${minutes}h`;
    } catch (error) {
      console.error(
        "Error formateando fecha:",
        error,
        "Fecha recibida:",
        fechaStr
      );
      return "Fecha inválida";
    }
  };

  const estadoOptions = [
    { value: "", label: "Seleccionar estado" },
    { value: "en estudio", label: "En estudio" },
    { value: "valido", label: "Viable" },
    { value: "rechazado", label: "No viable" },
    { value: "oculto", label: "Oculto" },
    { value: "norecomendado", label: "No recomendado" },
  ];

  const visibleEstadoOptions =
    userRole === "cliente"
      ? estadoOptions.filter((opt) => opt.value !== "oculto")
      : estadoOptions;

  const abortControllerRef = useRef<AbortController | null>(null);

  /* estado botón Excel */
  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  /* ---------- UTIL ---------- */
  const cleanNumberString = (numStr: string): string =>
    numStr ? numStr.replace(/\./g, "").replace(",", ".") : "";

  const updateCerradosParam = (mode: "abiertos" | "cerrados") => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("cerrados", mode === "cerrados" ? "true" : "false");
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  /* ---------- FETCH ---------- */
  const loadFavoritos = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setFavoritos(null);

    try {
      const params: FetchFavoritosParams = { page, limit };
      const get = (k: string) => searchParams.get(k) ?? undefined;

      if (get("search")) params.search = get("search")!;
      if (get("fechaInicio")) params.fechaInicio = get("fechaInicio")!;
      if (get("fechaFin")) params.fechaFin = get("fechaFin")!;
      if (get("importeDesde")) params.importeDesde = get("importeDesde")!;
      if (get("importeHasta")) params.importeHasta = get("importeHasta")!;
      if ((userRole === "admin" || userRole === "tecnico") && get("autor")) {
        params.autor = get("autor")!;
      }

      if ((userRole === "admin" || userRole === "tecnico") && get("asignado")) {
        params.asignado = get("asignado")!;
      }

      if (
        (userRole === "admin" || userRole === "tecnico") &&
        get("noRespondidos")
      ) {
        params.noRespondidos = get("noRespondidos")!;
      }

      params.cerrados = get("cerrados") ?? "false";
      if (get("estado")) {
        params.estado = get("estado")!;
      }

      const resp = await fetchFavoritos(params, controller.signal);
      setFavoritos(resp.data as IFavoritoConMensajes[]);
      setTotal(resp.total);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("Error al obtener favoritos:", err);
        alert(`Error al obtener favoritos: ${err.message ?? err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    /* primera vez añadimos cerrados=false si falta */
    if (!searchParams.get("cerrados")) {
      const p = new URLSearchParams(searchParams);
      p.set("cerrados", "false");
      p.set("page", "1");
      setSearchParams(p);
      return;
    }
    loadFavoritos();
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ---------- HANDLERS ---------- */

  const handleFilter = () => {
    const p = new URLSearchParams();
    if (localSearch) p.set("search", localSearch);
    if (localFechaInicio) p.set("fechaInicio", localFechaInicio);
    if (localFechaFin) p.set("fechaFin", localFechaFin);
    if (localImporteDesde)
      p.set("importeDesde", cleanNumberString(localImporteDesde));
    if (localImporteHasta)
      p.set("importeHasta", cleanNumberString(localImporteHasta));
    if ((userRole === "admin" || userRole === "tecnico") && localAutor.trim()) {
      p.set("autor", localAutor.trim());
    }
    if (
      (userRole === "admin" || userRole === "tecnico") &&
      localAsignado.trim()
    ) {
      p.set("asignado", localAsignado.trim());
    }
    if (localEstado) p.set("estado", localEstado);
    if (
      (userRole === "admin" || userRole === "tecnico") &&
      localNoRespondidos
    ) {
      p.set("noRespondidos", "true");
    }
    p.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    p.set("page", "1");
    setSearchParams(p);
    
  };

  const handleResetFilters = () => {
    setLocalSearch("");
    setLocalFechaInicio("");
    setLocalFechaFin("");
    setLocalImporteDesde("");
    setLocalImporteHasta("");
    setLocalAutor("");
    setLocalEstado("");
    setLocalAsignado("");
    setLocalNoRespondidos(false);
    const p = new URLSearchParams();
    p.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    p.set("page", "1");
    setSearchParams(p);
  };

  const handleEstadoChange = async (
    idConcurso: string,
    idUsuario: string,
    nuevoEstado: string
  ) => {
    try {
      await updateEstadoFavorito(idConcurso, idUsuario, nuevoEstado);

      // Actualizamos solo el item afectado
      setFavoritos((prev) =>
        prev
          ? prev.map((f) =>
              f.id_concurso === idConcurso && f.id_usuario === idUsuario
                ? {
                    ...f,
                    estado_soporte:
                      nuevoEstado === "En estudio" ? null : nuevoEstado,
                  }
                : f
            )
          : prev
      );
    } catch (err) {
      console.error("Error al actualizar estado:", err);
      alert("Error al actualizar el estado");
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setExcelStatus("loading");
      const token = getAuthToken();
      if (!token) {
        alert("No se encontró el token. Inicia sesión primero.");
        setExcelStatus("idle");
        return;
      }
      const url = `${window.location.protocol}//${window.location.hostname}/api/favoritos/export`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "favoritos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      setExcelStatus("success");
      setTimeout(() => setExcelStatus("idle"), 2000);
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error al exportar los favoritos a Excel.");
      setExcelStatus("idle");
    }
  };

  const renderExcelButtonContent = () => {
    if (excelStatus === "loading")
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando Excel...
        </>
      );
    if (excelStatus === "success")
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  /* -------------------------------------------------- */
  /* ---- FILTRADO DE OCULTOS PARA CLIENTE ------------ */
  /* -------------------------------------------------- */
  // Si el rol es 'cliente', filtramos aquellos con estado_soporte === 'oculto'
  const visibleFavoritos: IFavoritoConMensajes[] | null = favoritos
    ? userRole === "cliente"
      ? favoritos.filter((f) => f.estado_soporte?.toLowerCase() !== "oculto")
      : favoritos
    : null;

  /* =================================================== */
  /* =================  RENDER  ========================= */
  /* =================================================== */

  return (
    <div className="container-concursos">
      <h2>
        {userRole === "admin" || userRole === "tecnico"
          ? "Favoritos de todos los clientes"
          : "Mis Favoritos"}
      </h2>

      {/* ---------- botones abiertos / cerrados ---------- */}
      <div style={{ marginBottom: "1rem" }}>
        <button
          onClick={() => {
            setViewMode("abiertos");
            updateCerradosParam("abiertos");
          }}
          style={{
            marginRight: "1rem",
            backgroundColor: viewMode === "abiertos" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Favoritos Abiertos
        </button>
        <button
          onClick={() => {
            setViewMode("cerrados");
            updateCerradosParam("cerrados");
          }}
          style={{
            backgroundColor: viewMode === "cerrados" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Cerrados
        </button>
      </div>

      {/* ------------------- FILTROS ------------------- */}
      <div className="filtros">
        <input
          type="text"
          className="form-control busqueda"
          placeholder="Buscar (título/expediente)..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
        />

        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por cliente..."
            value={localAutor}
            onChange={(e) => setLocalAutor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}

        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por persona asignada..."
            value={localAsignado}
            onChange={(e) => setLocalAsignado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}

        <AmountDropdown
          desde={localImporteDesde}
          hasta={localImporteHasta}
          onChange={(d, h) => {
            setLocalImporteDesde(d);
            setLocalImporteHasta(h);
          }}
          placeholder="Importe del concurso"
        />

        <DatePicker
          selected={localFechaInicio ? new Date(localFechaInicio) : undefined}
          onChange={(dates: [Date | null, Date | null]) => {
            const [start, end] = dates;
            if (start) {
              const d = new Date(
                start.getTime() - start.getTimezoneOffset() * 60000
              );
              setLocalFechaInicio(d.toISOString().split("T")[0]);
            } else setLocalFechaInicio("");

            if (end) {
              const d = new Date(
                end.getTime() - end.getTimezoneOffset() * 60000
              );
              setLocalFechaFin(d.toISOString().split("T")[0]);
            } else setLocalFechaFin("");
          }}
          startDate={localFechaInicio ? new Date(localFechaInicio) : undefined}
          endDate={localFechaFin ? new Date(localFechaFin) : undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Fecha de alta (favorito)"
        />

        <select
          className="form-control"
          value={localEstado}
          onChange={(e) => setLocalEstado(e.target.value)}
          style={{ marginLeft: "0.5rem", maxWidth: "160px" }}
        >
          {visibleEstadoOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {(userRole === "admin" || userRole === "tecnico") && (
          <div className="checkbox-filtro">
            <label>
              <input
                type="checkbox"
                checked={localNoRespondidos}
                onChange={(e) => setLocalNoRespondidos(e.target.checked)}
              />
              No respondidos
            </label>
          </div>
        )}

        <button
          onClick={handleFilter}
          className="btn btn-primary"
          disabled={loading}
        >
          Filtrar
        </button>
        <button
          onClick={handleResetFilters}
          className="btn btn-secondary"
          disabled={loading}
        >
          Resetear
        </button>
      </div>

      {/* ----------------- EXPORT EXCEL ---------------- */}
      {(userRole === "admin" || userRole === "tecnico") && (
        <div style={{ margin: "1rem 0" }}>
          <button
            onClick={handleDownloadExcel}
            className="btn btn-success"
            disabled={excelStatus === "loading"}
          >
            {renderExcelButtonContent()}
          </button>
        </div>
      )}

      {/* ------------------ LISTADO -------------------- */}
      <div className="listing-container" style={{ marginTop: "1rem" }}>
        {loading || visibleFavoritos === null ? (
          <LoadingLottie />
        ) : visibleFavoritos.length === 0 ? (
          <p>No hay concursos favoritos.</p>
        ) : (
          /* visibleFavoritos
            .sort((a, b) => {
              const pa = getEmisorPriority(a, userRole || "");
              const pb = getEmisorPriority(b, userRole || "");
              if (pa !== pb) return pa - pb;
              return (
                (new Date(b.ultima_fecha_msg ?? 0).getTime() || 0) -
                (new Date(a.ultima_fecha_msg ?? 0).getTime() || 0)
              );
            })
            .map((concurso, idx) => { */
          visibleFavoritos.map((concurso, idx) => {
            /* -------- importe -------- */
            const total = Number(concurso.importe_total ?? 0);
            const estim = Number(concurso.importe_estimado ?? 0);
            const importe = total > 0 ? total : estim;
            const importeTxt =
              importe > 0
                ? new Intl.NumberFormat("es-ES", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: importe % 1 === 0 ? 0 : 2,
                  }).format(importe)
                : "N/A";

            /* estado soporte */
            const estadoVisible = mapEstadoSoporte(concurso.estado_soporte);
            const estadoSlug = slugSoporte(concurso.estado_soporte);
            const glow = getBackgroundColor(concurso, userRole || "");

            // Dentro de tu .map((concurso, idx) => { … })
            const fechaFinRaw = concurso.fecha_fin_ofertas;
            const fechaFin = fechaFinRaw
              ? new Date(fechaFinRaw).toLocaleDateString()
              : "—";

            return (
              <div
                key={`${concurso.id_concurso}-${idx}`}
                className="listing-item-wrapper"
                style={{ boxShadow: `0 0 15px 2px ${glow}` }}
              >
                <div className="listing-item">
                  <div className="info">
                    <div className="header-concurso">
                      <div className="expediente">
                        {concurso.codigo_expediente}
                      </div>

                      <div className="subheader-concurso">
                        <div
                          className={`estado estado-${estadoSlug}`}
                          title="Estado del soporte"
                        >
                          {estadoVisible}
                        </div>
                        <div className="publication-date">
                          {concurso.fecha_publicacion
                            ? new Date(
                                concurso.fecha_publicacion
                              ).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>

                    <div className="title">{concurso.titulo}</div>

                    <div className="details">
                      <div>
                        <FontAwesomeIcon icon={faLandmark} className="icon" />{" "}
                        {concurso.entidad_compradora}
                      </div>
                      <div>
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="icon"
                        />{" "}
                        {concurso.pais} - {concurso.localidad}
                      </div>
                    </div>

                    <div className="extra-info">
                      <div className="fin-plazo">
                        {concurso.fecha_fin_ofertas ? (
                          <>
                            <span>Hasta&nbsp;</span>
                            <span>
                              {formatearFecha(concurso.fecha_fin_ofertas)}
                            </span>
                          </>
                        ) : (
                          <span>Fecha fin no disponible</span>
                        )}
                      </div>
                    </div>

                    {(userRole === "admin" || userRole === "tecnico") &&
                      concurso.cliente_nombre && (
                        <div style={{ marginTop: ".8rem" }}>
                          <FontAwesomeIcon
                            icon={faUser}
                            style={{ marginRight: ".4rem" }}
                          />
                          <strong>Cliente:</strong>{" "}
                          <em>{concurso.cliente_nombre}</em>
                        </div>
                      )}
                  </div>

                  {/* BLOQUE PARA TODOS: Fecha de alta del favorito con fecha y hora */}
                  {concurso.fecha_alta_favorito && (
                    <div
                      style={{
                        marginBottom: ".8rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        style={{ marginRight: ".4rem" }}
                      />
                      <strong>Fecha alta:</strong>{" "}
                      <em>
                        {new Date(
                          concurso.fecha_alta_favorito
                        ).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}{" "}
                        a las{" "}
                        {new Date(
                          concurso.fecha_alta_favorito
                        ).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </em>
                    </div>
                  )}

                  <div className="footer-concurso">
                    <div className="importe">{importeTxt}</div>

                    <div
                      className="actions-concurso"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {/* ----- Badge de “Asignado a” para admin/tecnico ----- */}
                      {(userRole === "admin" || userRole === "tecnico") && (
                        <div
                          style={{
                            marginRight: "0.5rem",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            backgroundColor: "rgba(0, 123, 255, 0.15)", // color azul suave y semitransparente
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Asignado a {concurso.dueno_nombre ?? "nadie"}
                        </div>
                      )}

                      {/* ----- Dropdown de estado ----- */}
                      {(userRole === "admin" || userRole === "tecnico") && (
                        <select
                          className="form-control estado-dropdown"
                          style={{ marginRight: ".5rem" }}
                          value={
                            concurso.estado_soporte === null
                              ? "En estudio"
                              : concurso.estado_soporte
                          }
                          onChange={(e) =>
                            handleEstadoChange(
                              concurso.id_concurso,
                              concurso.id_usuario,
                              e.target.value
                            )
                          }
                        >
                          <option value="En estudio">En estudio</option>
                          <option value="valido">Viable</option>
                          <option value="rechazado">No viable</option>
                          <option value="oculto">Oculto</option>
                          <option value="norecomendado">No recomendado</option>
                        </select>
                      )}

                      {userRole === "cliente" && (
                        <FontAwesomeIcon
                          icon={faHeart}
                          style={{
                            color: "#B90029",
                            cursor: "not-allowed",
                            opacity: 0.5,
                          }}
                        />
                      )}

                      <Link
                        to={`/favorito/${concurso.id_concurso}/${concurso.id_usuario}`}
                      >
                        <FontAwesomeIcon icon={faEye} title="Ver más" />
                      </Link>

                      {concurso.enlace && (
                        <a
                          href={concurso.enlace}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon
                            icon={faLink}
                            title="Enlace oficial"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ----------------- PAGINACIÓN ------------------ */}
      <div className="pagination-concursos" style={{ marginTop: "1rem" }}>
        <button
          onClick={() =>
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: String(page - 1),
            })
          }
          disabled={page <= 1}
        >
          Anterior
        </button>
        <span>
          Página {page} de {maxPages}
        </span>
        <button
          onClick={() =>
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: String(page + 1),
            })
          }
          disabled={page >= maxPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Favoritos;
